import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const reverseRotate = keyframes`
  0% { transform: rotate(360deg); }
  100% { transform: rotate(0deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(0.98); }
  50% { opacity: 0.85; transform: scale(1.02); }
`;

const StyledLoader = styled.div`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--dark-navy);
  z-index: 9999;
  overflow: hidden;
  transition: opacity 0.5s cubic-bezier(0.645, 0.045, 0.355, 1),
              transform 0.5s cubic-bezier(0.645, 0.045, 0.355, 1);
  opacity: ${props => (props.$isExiting ? 0 : 1)};
  transform: ${props => (props.$isExiting ? 'scale(1.05)' : 'scale(1)')};
  pointer-events: ${props => (props.$isExiting ? 'none' : 'auto')};

  /* Subtle futuristic Grid Backdrop */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(201, 169, 110, 0.03) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(201, 169, 110, 0.03) 1px, transparent 1px);
    background-size: 40px 40px;
    mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
    pointer-events: none;
  }

  /* Radial ambient glow */
  &::after {
    content: '';
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(201, 169, 110, 0.08) 0%, transparent 70%);
    animation: ${pulseGlow} 4s ease-in-out infinite;
    pointer-events: none;
  }

  .hud-wrapper {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .spinner-box {
    position: relative;
    width: 140px;
    height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .outer-ring {
    position: absolute;
    inset: 0;
    animation: ${rotate} 12s linear infinite;
  }

  .inner-ring {
    position: absolute;
    inset: 18px;
    animation: ${reverseRotate} 8s linear infinite;
  }

  .counter-text {
    font-family: var(--font-mono);
    font-size: 22px;
    font-weight: 700;
    color: var(--green);
    letter-spacing: 0.1em;
    text-shadow: 0 0 12px rgba(201, 169, 110, 0.4);
  }

  .telemetry-panel {
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    font-family: var(--font-mono);
  }

  .status-tag {
    font-size: 11px;
    letter-spacing: 0.2em;
    color: var(--slate);
    text-transform: uppercase;
  }

  .status-line {
    font-size: 13px;
    letter-spacing: 0.12em;
    color: var(--lightest-slate);
    display: flex;
    align-items: center;
    gap: 8px;

    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: var(--green);
      box-shadow: 0 0 8px var(--green);
    }
  }

  .progress-bar-track {
    margin-top: 18px;
    width: 180px;
    height: 2px;
    background: rgba(201, 169, 110, 0.15);
    border-radius: 2px;
    overflow: hidden;
    position: relative;

    .progress-bar-fill {
      height: 100%;
      background: var(--green);
      box-shadow: 0 0 10px var(--green);
      transition: width 0.15s ease-out;
    }
  }
`;

const Loader = ({ finishLoading }) => {
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('SYS.INIT // CORE_ONLINE');
  const [isExiting, setIsExiting] = useState(false);
  const isRobotReadyRef = useRef(false);

  useEffect(() => {
    // Only run loader on Desktop (width >= 769px)
    if (typeof window !== 'undefined' && window.innerWidth < 769) {
      finishLoading();
      return undefined;
    }

    const handleRobotReady = () => {
      isRobotReadyRef.current = true;
      setStatusMessage('SYSTEM READY // HERO_UNLOCKED');
    };

    window.addEventListener('hero-robot-ready', handleRobotReady);

    // Progress counter ticker
    const interval = setInterval(() => {
      setProgress(prev => {
        if (isRobotReadyRef.current) {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return Math.min(100, prev + 8);
        }
        // Smoothly progress up to 85% while waiting for robot model
        if (prev < 85) {
          if (prev < 30) {
            setStatusMessage('SYS.INIT // CORE_ONLINE');
          } else if (prev < 65) {
            setStatusMessage('GEOMETRY.FETCH // 3D_ROBOT_SCENE');
          } else {
            setStatusMessage('COMPOSITING // WEBGL_CANVAS');
          }
          return prev + Math.floor(Math.random() * 4 + 2);
        }
        return 85;
      });
    }, 50);

    // Safety timeout: max 5.5s
    const safetyTimeout = setTimeout(() => {
      isRobotReadyRef.current = true;
      setStatusMessage('SYSTEM READY // HERO_UNLOCKED');
    }, 5500);

    return () => {
      clearInterval(interval);
      clearTimeout(safetyTimeout);
      window.removeEventListener('hero-robot-ready', handleRobotReady);
    };
  }, [finishLoading]);

  // When progress reaches 100%, trigger exit transition
  useEffect(() => {
    if (progress >= 100 && !isExiting) {
      setIsExiting(true);
      const exitTimer = setTimeout(() => {
        finishLoading();
      }, 500);
      return () => clearTimeout(exitTimer);
    }
  }, [progress, isExiting, finishLoading]);

  return (
    <StyledLoader className="loader" $isExiting={isExiting}>
      <Helmet bodyAttributes={{ class: `hidden` }} />

      <div className="hud-wrapper">
        <div className="spinner-box">
          <svg className="outer-ring" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke="rgba(201, 169, 110, 0.2)"
              strokeWidth="1.5"
            />
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke="#c9a96e"
              strokeWidth="1.5"
              strokeDasharray="40 180"
              strokeLinecap="round"
            />
          </svg>

          <svg className="inner-ring" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke="#c9a96e"
              strokeWidth="1"
              strokeDasharray="25 80"
              strokeOpacity="0.6"
            />
          </svg>

          <div className="counter-text">{progress}%</div>
        </div>

        <div className="telemetry-panel">
          <span className="status-tag">[ HERO_LOADER_HUD ]</span>
          <div className="status-line">
            <span className="dot" />
            <span>{statusMessage}</span>
          </div>
          <div className="progress-bar-track">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
    </StyledLoader>
  );
};

Loader.propTypes = {
  finishLoading: PropTypes.func.isRequired,
};

export default Loader;
